/**
 * Iter 246 — Fiche profil d'un membre d'équipe (tenant_admin uniquement).
 *
 * Affiche :
 * - Identité (nom, email, rôle) + bouton retour
 * - Bloc actions rapides : changer rôle, supprimer
 * - Panneau Autorisations détaillées (groupé par module activé)
 *   → Toggle on/off, sauvegarde via PUT /tenant/members/:id/permissions
 *
 * Accessible via /tenant/team/:userId
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  ArrowLeft, Loader2, Save, Crown, User, Shield, Trash2,
  CheckCircle2, AlertCircle, Eye,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";

const PERM_BADGE = {
  true: { label: "Activé", color: "#22c55e" },
  false: { label: "Désactivé", color: "#94a3b8" },
};

export default function MemberProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { axiosAuth, user: currentUser } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);

  const [member, setMember] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [postes, setPostes] = useState([]);
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [memberRes, catalogRes, postesRes] = await Promise.all([
        api.get(`/tenant/members/${userId}/permissions`),
        api.get(`/tenant/permissions/catalog`),
        api.get(`/tenant/postes`),
      ]);
      setMember(memberRes.data);
      setPerms(memberRes.data.module_permissions || {});
      setCatalog(catalogRes.data.items || []);
      setPostes(postesRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger ce membre");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const togglePerm = (key) => {
    setPerms(p => ({ ...p, [key]: !p[key] }));
    setDirty(true);
  };

  const applyPoste = async (posteId) => {
    const p = postes.find(x => x.poste_id === posteId);
    if (!p) return;
    if (!window.confirm(`Rattacher ce membre au poste "${p.name}" ?\n\nLes droits du poste remplaceront les permissions actuelles.`)) return;
    try {
      const r = await api.post(`/tenant/members/${userId}/poste/assign`, { poste_id: posteId });
      setPerms(r.data.module_permissions);
      setDirty(false);
      toast.success(`Poste "${p.name}" appliqué`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/tenant/members/${userId}/permissions`, {
        module_permissions: perms,
      });
      setPerms(r.data.module_permissions);
      toast.success("Autorisations mises à jour");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (newRole) => {
    if (member.user_id === currentUser?.user_id) {
      toast.error("Vous ne pouvez pas changer votre propre rôle");
      return;
    }
    if (!window.confirm(`Confirmer le changement de rôle vers "${newRole === "tenant_admin" ? "Admin" : "Membre"}" ?`)) return;
    try {
      await api.put(`/tenant/members/${userId}/role`, { role: newRole });
      toast.success("Rôle mis à jour");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const removeMember = async () => {
    if (member.user_id === currentUser?.user_id) {
      toast.error("Vous ne pouvez pas vous supprimer vous-même");
      return;
    }
    if (!window.confirm(`Supprimer définitivement ${member.name} ?`)) return;
    try {
      await api.delete(`/tenant/members/${userId}`);
      toast.success("Membre supprimé");
      navigate("/tenant/team");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // Iter 246.4 — "Voir en tant que..." : tenant_admin impersonates one of his members
  const viewAs = async () => {
    if (member.role === "tenant_admin") {
      toast.error("Impossible d'impersonifier un autre administrateur");
      return;
    }
    if (!window.confirm(`Basculer en mode "Voir en tant que ${member.name}" ?\n\nVotre session admin sera mise en pause. Utilisez le bandeau orange en haut pour revenir.`)) return;
    try {
      const r = await api.post(`/tenant/members/${userId}/impersonate`);
      // Stash current admin token in case AuthContext.logout doesn't restore it
      localStorage.setItem("blitzz_admin_token", localStorage.getItem("token") || localStorage.getItem("auth_token") || "");
      localStorage.setItem("token", r.data.token);
      localStorage.setItem("auth_token", r.data.token);
      toast.success(`Connecté en tant que ${member.name}`);
      // Hard refresh so /auth/me reloads with new perms + sidebar refreshes
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  }
  if (!member) {
    return <div className="p-8 text-center text-slate-500">Membre introuvable</div>;
  }

  const isAdmin = member.role === "tenant_admin";
  const RoleIcon = isAdmin ? Crown : User;

  return (
    <div className="p-6 max-w-[1100px] mx-auto space-y-5" data-testid="member-profile">
      <Button variant="ghost" size="sm" onClick={() => navigate("/tenant/team")} className="text-slate-400 -mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'équipe
      </Button>

      <PageHeader
        icon={<Shield />}
        title={member.name || member.email}
        description={member.email}
        actions={
          <div className="flex gap-2">
            {!isAdmin && member.user_id !== currentUser?.user_id && (
              <Button onClick={viewAs} variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" data-testid="btn-view-as">
                <Eye className="w-4 h-4 mr-2" /> Voir en tant que
              </Button>
            )}
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]"
              data-testid="btn-save-permissions"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {dirty ? "Enregistrer les modifications" : "Enregistré"}
            </Button>
          </div>
        }
      />

      {/* Bloc identité + rôle */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="member-identity">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Identité & rôle</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Nom" value={member.name || "—"} />
          <Field label="Email" value={member.email} />
          <div>
            <div className="text-[11px] text-slate-500 uppercase mb-1">Rôle dans le tenant</div>
            <div className="flex items-center gap-2">
              <div className={`px-2.5 py-1 rounded text-xs font-bold uppercase flex items-center gap-1.5`}
                   style={{ background: isAdmin ? "#3b82f620" : "#22c55e20", color: isAdmin ? "#3b82f6" : "#22c55e" }}>
                <RoleIcon className="w-3 h-3" />
                {isAdmin ? "Admin Tenant" : "Membre"}
              </div>
              {member.user_id !== currentUser?.user_id && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => changeRole(isAdmin ? "tenant_member" : "tenant_admin")}
                  className="text-xs text-cyan-400"
                  data-testid="btn-toggle-role"
                >
                  {isAdmin ? "Rétrograder en membre" : "Promouvoir en admin"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Panneau Permissions */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="permissions-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Autorisations détaillées</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isAdmin
                ? "⚠️ Les Admins ont un accès complet à tous les modules activés (non modifiable)."
                : `${Object.values(perms).filter(Boolean).length} permission(s) activée(s) sur ${Object.keys(perms).length}`}
            </p>
          </div>
          {dirty && (
            <div className="flex items-center gap-2 text-xs text-amber-400">
              <AlertCircle className="w-4 h-4" />
              Modifications non enregistrées
            </div>
          )}
        </div>

        {/* Presets métier (Iter 246.1) */}
        {!isAdmin && postes.length > 0 && (
          <div className="bg-black/30 border border-white/5 rounded-lg p-3 mb-4" data-testid="preset-bar">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Rattacher à un poste (applique ses droits en 1 clic)</div>
            <div className="flex flex-wrap gap-2">
              {postes.map((p) => {
                const Icon = LucideIcons[p.icon] || LucideIcons.Briefcase;
                return (
                  <button
                    key={p.poste_id}
                    onClick={() => applyPoste(p.poste_id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-white/5 transition-colors"
                    style={{ borderColor: `${p.color}40`, color: p.color }}
                    title={p.description}
                    data-testid={`poste-apply-${p.poste_id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {p.name}
                    <span className="opacity-50 ml-1">({(p.grants || []).length})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {catalog.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">
            Aucun module activé sur ce tenant. Activez d'abord les modules dans Master Admin.
          </div>
        )}

        <div className="space-y-4">
          {catalog.map((mod) => {
            const Icon = LucideIcons[mod.icon] || LucideIcons.Folder;
            return (
              <div key={mod.module} className="bg-black/20 rounded-lg p-4" data-testid={`module-${mod.module}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded bg-[#f7c935]/15 text-[#f7c935] flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <h3 className="text-white font-bold">{mod.label}</h3>
                </div>
                <div className="space-y-2">
                  {mod.permissions.map((p) => {
                    const val = !!perms[p.key];
                    const disabled = isAdmin || (p.admin_only && !isAdmin);
                    return (
                      <div
                        key={p.key}
                        className={`flex items-center justify-between gap-3 px-3 py-2 rounded ${disabled ? "opacity-50" : "hover:bg-white/[0.02]"}`}
                        data-testid={`perm-${p.key}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white">{p.label}</div>
                          <div className="text-[10px] text-slate-600 font-mono">{p.key}</div>
                        </div>
                        {p.admin_only && !isAdmin && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-bold uppercase">Admin only</span>
                        )}
                        <Switch
                          checked={isAdmin ? true : val}
                          disabled={disabled}
                          onCheckedChange={() => !disabled && togglePerm(p.key)}
                          data-testid={`switch-${p.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Zone danger */}
      {member.user_id !== currentUser?.user_id && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5" data-testid="danger-zone">
          <h2 className="text-sm font-bold text-red-400 uppercase tracking-wider mb-2">Zone de danger</h2>
          <p className="text-xs text-slate-400 mb-3">Cette action est irréversible.</p>
          <Button onClick={removeMember} variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="btn-remove-member">
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer ce membre
          </Button>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] text-slate-500 uppercase mb-1">{label}</div>
    <div className="text-sm text-white">{value}</div>
  </div>
);
